import { forwardRef } from "react";
import PropTypes from "prop-types";
import { useTheme, styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";

const CustomTextFieldRoot = styled(TextField)(({ theme, ownerState }) => {
  const { palette = {}, borders = {}, borderRadius: borderRadiusTheme = {}} = theme;
  const {
    variant = "outlined",
    bgColor = "light",
    color = "dark",
    borderRadius = "medium",
    border = false,
    size = "medium",
    fullWidth = true,
    error = false,
    success = false,
  } = ownerState;

  // Border radius value
  const borderRadiusValue =
    (borderRadius && borderRadiusTheme[borderRadius]) ||
    borderRadiusTheme?.medium || // default fallback
    theme.shape?.borderRadius || // MUI default
    '0px';

  // Color value
  let colorValue = palette[color]?.main || color;
  if (error) colorValue = palette.error.main;
  if (success) colorValue = palette.success.main;

  // Border styles
  const borderValue = border ? `2px solid ${palette.gray?.main || "#CFCFCF"}` : "none";

  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: borderRadiusValue,
      backgroundColor: bgColor === "transparent" ? "transparent" : palette[bgColor]?.main || bgColor,
      border: borderValue,
      "& fieldset": {
        borderColor: colorValue,
      },
      "&:hover fieldset": {
        borderColor: colorValue,
        borderWidth: 2,
      },
      "&.Mui-focused fieldset": {
        borderColor: colorValue,
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root": {
      color: colorValue,
    },
    "& .MuiInputBase-input": {
      color: colorValue,
      padding: size === "small" ? "8px 12px" : "12px 14px",
    },
    width: fullWidth ? "100%" : "auto",
  };
});

const CustomTextField = forwardRef((props, ref) => {
  const {
    variant = "outlined",
    bgColor = "light",
    color = "dark",
    borderRadius = "medium",
    border = false,
    size = "medium",
    fullWidth = true,
    error = false,
    success = false,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  return (
    <CustomTextFieldRoot
      ref={ref}
      ownerState={{
        variant,
        bgColor,
        color,
        borderRadius,
        border,
        size,
        fullWidth,
        error,
        success,
      }}
      sx={sx}
      {...rest}
    />
  );
});

CustomTextField.propTypes = {
  variant: PropTypes.oneOf(["outlined", "filled", "standard"]),
  bgColor: PropTypes.string,
  color: PropTypes.string,
  borderRadius: PropTypes.string,
  border: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  success: PropTypes.bool,
  sx: PropTypes.object,
};

export default CustomTextField;